import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './DivisionSelector.module.css';

interface DivisionSelectorProps {
    selectedDivision: string;
    onDivisionChange: (division: string) => void;
    isDark: boolean;
    gameStates?: {
        '1div'?: { isComplete: boolean; isWon: boolean; guesses: number };
        '2div'?: { isComplete: boolean; isWon: boolean; guesses: number };
        '3div'?: { isComplete: boolean; isWon: boolean; guesses: number };
        '4div'?: { isComplete: boolean; isWon: boolean; guesses: number };
    };
}

const DivisionSelector: React.FC<DivisionSelectorProps> = ({
    selectedDivision,
    onDivisionChange,
    isDark,
    gameStates
}) => {
    const { t } = useTranslation('pages');

    const divisions = [
        { id: '1div', labelKey: '1div', shortLabel: '1' },
        { id: '2div', labelKey: '2div', shortLabel: '2' },
        { id: '3div', labelKey: '3div', shortLabel: '3' },
        { id: '4div', labelKey: '4div', shortLabel: '4' }
    ];

    const getButtonStatus = (divisionId: string) => {
        if (!gameStates) {
            return 'default';
        }
        const state = gameStates[divisionId as keyof typeof gameStates];
        if (!state) {
            return 'default';
        }
        if (state.isComplete && state.isWon) return 'won';
        if (state.isComplete && !state.isWon) return 'lost';
        if (state.guesses > 0) return 'in-progress';
        return 'default';
    };

    return (
        <div className={styles.selectorContainer}>
            <div className={styles.divisionBar}>
                {divisions.map((division) => {
                    const status = getButtonStatus(division.id);
                    return (
                        <button
                            key={division.id}
                            onClick={() => onDivisionChange(division.id)}
                            className={`${styles.divisionButton} ${
                                selectedDivision === division.id ? styles.active : ''
                            } ${styles[status]}`}
                            style={{
                                backgroundColor: selectedDivision === division.id 
                                    ? '#e74c3c' 
                                    : isDark ? '#2d2d2d' : '#ffffff',
                                color: selectedDivision === division.id 
                                    ? '#ffffff' 
                                    : isDark ? '#b8b8b8' : '#2c3e50',
                                borderColor: isDark ? '#505050' : '#dee2e6'
                            }}
                            title={t(`divisions.${division.labelKey}`)}
                        >
                            <span className={styles.desktopLabel}>{t(`divisions.${division.labelKey}`)}</span>
                            <span className={styles.mobileLabel}>{division.shortLabel}</span>
                            {status === 'won' && <span className={styles.statusIcon}>✓</span>}
                            {status === 'lost' && <span className={styles.statusIcon}>✗</span>}
                            {status === 'in-progress' && <span className={styles.statusIcon}>•</span>}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default DivisionSelector;